import React from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import BSL_Dialog from "../components/BSL_Dialog";
import * as BSL_Common from "../components/BSL_CommonControls";
import { IconButton } from "@material-ui/core";
import { Delete as DeleteIcon, Close as CloseIcon } from "@material-ui/icons";
import { Palette } from "../AppConfig";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";

// import all helpers
import {
    checkValidJSON,
    setDynamicsCode,
    extractLengths,
    extractPitch,
    buildWZColumns,
    getKeys,
    deleteExtra,
    findBestMatch,
    getGroupHeaders,
    lyvesCodeBuilder,
} from "./Helpers/DynamicsHelper";

const useStyles = makeStyles((theme) => ({
    root: {
        flex: 1,
        maxWidth: "90%",
        minWidth: "600px",
    },
    container: {
        height: "90%",
        maxHeight: "800px",
    },
    row: {
        "tr:hover": {
            backgroundColor: "rgba(7, 50, 50, 0.42)",
        },
    },
    rowCell: {
        fontSize: "12px",
        border: "none",
        padding: "2px 0px 2px 24px",
        color: theme.brand.FIELDERS ? "inherit" : Palette.Lysaght.MidGrey,
        "&:hover": {
            backgroundColor: "rgba(7, 177, 77, 0.42)",
            cursor: "pointer",
        },
    },
    categoryHeadingCell: {
        textAlign: "center",
        backgroundColor: Palette.FieldersOrange + " !important",
        padding: "2px !important",
        borderTopLeftRadius: "5px",
        borderTopRightRadius: "5px",
        borderLeft: "1px solid white !important",
        borderRight: "1px solid white !important",
        position: "sticky",
        top: "0px !important",
    },
    categoryHeadingCellBlank: {
        textAlign: "center",
        backgroundColor: "#ffffff !important",
        padding: "2px !important",
        position: "sticky",
        top: "0px !important",
    },
    headingCell: {
        fontSize: "11px",
        border: "none",
        textTransform: "uppercase",
        color: "#ffffff",
        fontWeight: theme.brand.FIELDERS ? "inherit" : "600",
        fontFamily: theme.typography.fontFamilySecondary,
        backgroundColor: Palette.FieldersBlue,
        lineHeight: "normal",
        zIndex: "5",
        top: "15px",
    },
    headingRow: {
        backgroundColor: Palette.FieldersBlue,
    },
    editor: {
        border: "1px solid grey",
        width: "100px",
    },
}));

export default function ManageDynamicCodes(props) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { row, saveValue, onClose, materials } = props;

    const [matchedItems, setMatchedItems] = React.useState([...row.MatchedItems]);
    const [displayData, setDisplayData] = React.useState(JSON.stringify(row.MatchedItems, null, 4));
    const [displayDataValidJSON, setDisplayDataValidJSON] = React.useState(true);
    const [edit, setEdit] = React.useState(null);

    const isDeg = row?.ProductCode.includes("@");

    const promptLengths = extractLengths(row?.PromptTextCodes);
    const promptPitch = extractPitch(row?.PromptTextCodes);
    const wz_columns = buildWZColumns(isDeg ? promptPitch : promptLengths, isDeg);
    const keys = getKeys(row, materials);

    // Precompute all cellText values to find duplicates
    const allCells = [];
    keys.forEach((key) => {
        wz_columns.forEach((column) => {
            const directValue = matchedItems[key]?.[column.id];
            const matchedArray = Object.values(matchedItems);
            const bestMatch = findBestMatch(matchedArray, key, column.id, row?.ProductCode);
            const inherit_value = bestMatch?.dynamicsCode || "";

            let cellText = "";
            if (column.id === "Key") {
                cellText = key;
            } else if (directValue !== undefined) {
                cellText = directValue;
            } else if (inherit_value) {
                cellText = inherit_value;
            } else {
                cellText = "❌";
            }

            allCells.push(cellText);
        });
    });

    const freqMap = allCells.reduce((acc, txt) => {
        acc[txt] = (acc[txt] || 0) + 1;
        return acc;
    }, {});

    return (
        <BSL_Dialog
            title={"Edit Dynamic Codes for " + row?.ProductCode}
            open={true}
            button1Text="Update"
            button1Click={() => {
                if (!checkValidJSON(displayData)) {
                    alert("JSON is not valid. Fix it first.");
                } else {
                    saveValue(row, "MatchedItems", matchedItems);
                }
            }}
            button2Text="Cancel"
            button2Click={onClose}
            style={{ maxWidth: "initial" }}
            fullWidth={true}
            maxWidth={"xl"}
        >
            <BSL_Common.SectionRow>
                <BSL_Common.SectionColumn>
                    <TableContainer className={classes.container}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>{getGroupHeaders(wz_columns, classes)}</TableRow>
                                <TableRow className={classes.headingRow}>
                                    {wz_columns.map((column) =>
                                        column.hideInTable ? null : (
                                            <TableCell
                                                key={column.id + "_header"}
                                                align={column.align}
                                                style={column.style}
                                                className={classes.headingCell}
                                            >
                                                {column.label}
                                            </TableCell>
                                        )
                                    )}
                                    <TableCell key={"Options"} className={classes.headingCell} />
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {keys.map((key, rowIndex) => (
                                    <TableRow
                                        key={key}
                                        hover
                                        role="checkbox"
                                        tabIndex={-1}
                                        className={classes.row}
                                        style={{
                                            background: rowIndex % 2 === 0 ? "#e7ebee" : "#ffffff",
                                        }}
                                    >
                                        {wz_columns.map((column) => {
                                            const directValue = matchedItems[key]?.[column.id];
                                            const matchedArray = Object.values(matchedItems);
                                            const bestMatch = findBestMatch(
                                                matchedArray,
                                                key,
                                                column.id,
                                                row?.ProductCode
                                            );
                                            const inherit_value = bestMatch?.dynamicsCode || "";

                                            let cellText = "";
                                            if (column.id === "Key") {
                                                cellText = key;
                                            } else if (directValue !== undefined) {
                                                cellText = directValue;
                                            } else if (inherit_value) {
                                                cellText = inherit_value;
                                            } else {
                                                cellText = "❌";
                                            }

                                            const isInherited = directValue === undefined && column.id !== "Key";

                                            const expectedLyvesCode = lyvesCodeBuilder(
                                                row?.ProductCode,
                                                key,
                                                column.id
                                            );
                                            const mappedLyvesCode = bestMatch?.lyvesCode ?? expectedLyvesCode;

                                            const isDuplicate = freqMap[cellText] > 1;

                                            const currentDirectValue = matchedItems[mappedLyvesCode]?.[column.id];
                                            const initialEditValue =
                                                currentDirectValue !== undefined ? currentDirectValue : inherit_value;

                                            return (
                                                <TableCell
                                                    key={column.id + "_" + key}
                                                    className={classes.rowCell}
                                                    style={{
                                                        background: isDuplicate
                                                            ? "greenyellow"
                                                            : isInherited
                                                            ? "blanchedalmond"
                                                            : "transparent",
                                                    }}
                                                >
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                        {isInherited && inherit_value && (
                                                            <div
                                                                style={{
                                                                    color: "#f59758",
                                                                    marginTop: "-4px",
                                                                    fontSize: "8px",
                                                                    float: "left",
                                                                    whiteSpace: "nowrap",
                                                                    height: "10px",
                                                                }}
                                                            >
                                                                → inherited
                                                            </div>
                                                        )}

                                                        {edit === key + "," + column.id ? (
                                                            <div style={{ whiteSpace: "nowrap" }}>
                                                                <input
                                                                    autoFocus
                                                                    className={classes.editor}
                                                                    type="text"
                                                                    value={cellText}
                                                                    onChange={(e) => {
                                                                        const updated = setDynamicsCode(
                                                                            matchedItems,
                                                                            mappedLyvesCode,
                                                                            column.id,
                                                                            e.target.value
                                                                        );
                                                                        setMatchedItems(updated);
                                                                        setDisplayData(
                                                                            JSON.stringify(updated, null, 4)
                                                                        );
                                                                    }}
                                                                    onKeyPress={(e) => {
                                                                        if (e.key === "Escape" || e.key === "Enter") {
                                                                            setEdit(null);
                                                                        }
                                                                    }}
                                                                />
                                                                <IconButton
                                                                    aria-label="cancel edit"
                                                                    size="small"
                                                                    onClick={() => setEdit(null)}
                                                                    style={{ height: "16px", width: "16px" }}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                style={{ flex: 1, cursor: "pointer" }}
                                                                onClick={() => setEdit(key + "," + column.id)}
                                                            >
                                                                {cellText}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </BSL_Common.SectionColumn>

                <BSL_Common.SectionColumn
                    style={{
                        width: "25%",
                        minWidth: "300px",
                    }}
                >
                    <textarea
                        id="jsonEditor"
                        name="jsonEditor"
                        style={{
                            backgroundColor: "beige",
                            fontFamily: "monospace",
                            padding: "10px",
                            borderRadius: "9px",
                            color: "#444444",
                            maxHeight: "400px",
                            overflowY: "auto",
                            fontSize: "12px",
                        }}
                        rows="50"
                        cols="40"
                        onChange={(e) => {
                            const val = e.target.value;
                            setDisplayData(val);
                            if (checkValidJSON(val)) {
                                setMatchedItems(JSON.parse(val));
                            }
                        }}
                        onBlur={(e) => {
                            if (!checkValidJSON(e.target.value)) {
                                alert("JSON is not valid. Fix it first.");
                                e.preventDefault();
                                return false;
                            } else {
                                const parsed = JSON.parse(e.target.value);
                                setMatchedItems(parsed);
                                setDisplayData(JSON.stringify(parsed, null, 4));
                            }
                        }}
                        value={displayData}
                    />
                    <div
                        style={{
                            color: displayDataValidJSON ? "green" : "red",
                            fontSize: "12px",
                            padding: "10px",
                            textAlign: "center",
                        }}
                    >
                        {displayDataValidJSON ? "VALID JSON" : "NOT VALID JSON"}
                    </div>
                </BSL_Common.SectionColumn>
            </BSL_Common.SectionRow>
        </BSL_Dialog>
    );
}
